import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ThemeBackground from '../../components/Layout/ThemeBackground';
import api from '../../utils/api';

interface Agreement {
  _id: string;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

const SignAgreement: React.FC = () => {
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAgreement = async () => {
      try {
        const response = await api.get('/api/documents?category=agreement');
        if (response.data && response.data.length > 0) {
          // Get the first (and should be only) agreement document
          setAgreement(response.data[0]);
        } else {
          setError('No agreement document found');
        }
      } catch (error: any) {
        setError(error.response?.data?.error || 'Failed to load agreement');
      } finally {
        setLoading(false);
      }
    };

    fetchAgreement();
  }, []);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Check if scrolled to within 50px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const handleSign = async () => {
    if (!accepted) {
      setError('You must accept the terms to continue');
      return;
    }

    try {
      setSigning(true);
      setError(null);
      
      // Update counsellor record to mark contract as signed
      await api.put('/api/counsellors/me', {
        contractSigned: true,
        contractSignedDate: new Date()
      });
      
      setSuccess(true);
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/counsellor/dashboard');
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to sign agreement');
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <ThemeBackground variant="full">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeBackground>
    );
  }

  if (success) {
    return (
      <ThemeBackground variant="full">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', px: 2 }}>
          <Card sx={{ maxWidth: 600, backgroundColor: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.3)' }}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ color: '#4caf50', fontWeight: 600 }}>
                Agreement Signed Successfully!
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mt: 2 }}>
                Thank you for signing the counsellor agreement. You can now be assigned clients.
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
                Redirecting to your dashboard...
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </ThemeBackground>
    );
  }

  if (error && !agreement) {
    return (
      <ThemeBackground variant="full">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', px: 2 }}>
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            {error}
          </Alert>
        </Box>
      </ThemeBackground>
    );
  }

  return (
    <ThemeBackground variant="full">
      <Box sx={{ minHeight: '100vh', py: 4, px: 2 }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          {/* Header */}
          <Paper 
            sx={{ 
              p: 3, 
              mb: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DescriptionIcon sx={{ fontSize: 40, color: '#64b5f6', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                  {agreement?.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Last Updated: {new Date(agreement?.updatedAt || '').toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            <Alert 
              severity="info" 
              sx={{ 
                backgroundColor: '#e3f2fd',
                border: '2px solid #2196f3',
                color: '#000',
                '& .MuiAlert-icon': {
                  color: '#1976d2'
                }
              }}
            >
              <Typography sx={{ fontWeight: 600, color: '#000' }}>
                Please read the entire agreement carefully. You must scroll to the bottom before you can accept the terms.
              </Typography>
            </Alert>
          </Paper>

          {/* Agreement Content */}
          <Paper 
            ref={contentRef}
            onScroll={handleScroll}
            sx={{ 
              p: 4,
              mb: 3,
              maxHeight: '60vh',
              overflowY: 'auto',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(100, 181, 246, 0.5)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(100, 181, 246, 0.7)'
                }
              }
            }}
          >
            <Box
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: 1.8,
                fontSize: '1rem',
                '& h1': {
                  fontSize: '2rem',
                  fontWeight: 700,
                  marginTop: '1.5rem',
                  marginBottom: '1rem',
                  color: '#64b5f6'
                },
                '& h2': {
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  marginTop: '1.25rem',
                  marginBottom: '0.75rem',
                  color: '#64b5f6'
                },
                '& h3': {
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  marginTop: '1rem',
                  marginBottom: '0.5rem',
                  color: '#90caf9'
                },
                '& p': {
                  marginBottom: '1rem'
                },
                '& ul, & ol': {
                  marginLeft: '1.5rem',
                  marginBottom: '1rem'
                },
                '& li': {
                  marginBottom: '0.5rem'
                },
                '& strong': {
                  fontWeight: 700,
                  color: '#fff'
                },
                '& em': {
                  fontStyle: 'italic'
                },
                '& hr': {
                  border: 'none',
                  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                  margin: '2rem 0'
                }
              }}
            >
              <ReactMarkdown>{agreement?.content || ''}</ReactMarkdown>
            </Box>
          </Paper>

          {/* Acceptance Section */}
          <Paper 
            sx={{ 
              p: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  disabled={!hasScrolledToBottom || signing}
                  sx={{
                    color: 'rgba(100, 181, 246, 0.7)',
                    '&.Mui-checked': {
                      color: '#64b5f6'
                    }
                  }}
                />
              }
              label={
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  I have read and agree to the terms and conditions outlined in this agreement
                </Typography>
              }
              sx={{ mb: 2 }}
            />

            {!hasScrolledToBottom && (
              <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', border: '1px solid rgba(255, 152, 0, 0.3)' }}>
                Please scroll to the bottom of the agreement to enable the acceptance checkbox
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/counsellor/dashboard')}
                disabled={signing}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSign}
                disabled={!accepted || !hasScrolledToBottom || signing}
                sx={{
                  backgroundColor: '#4caf50',
                  '&:hover': {
                    backgroundColor: '#45a049'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    color: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                {signing ? <CircularProgress size={24} /> : 'Sign Agreement'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </ThemeBackground>
  );
};

export default SignAgreement;
